import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlags,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TableauUser,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { FlowOrderField } from '../../../gen-src/queries/globalTypes';
import { createLinkOrTextCellFormatter } from '../datagrid/cellFormatters/LinkCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

const shouldBeObfuscated: (rowData: FlowsDisplayGridRowData) => boolean = ({ name }) => name === null;
const projectContainerType = 'Project';

export interface FlowsDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly vizportalUrlId: string;
  readonly owner: TableauUser | null;
  readonly projectVizportalUrlId: string | null;
  readonly containerName: string | null;
  readonly containerType: string;
}

export function getRowDataForFlowWithoutPersonalSpace(flow: FlowsDisplayGridRowDataFragmentWithoutPersonalSpace): FlowsDisplayGridRowData {
  return {
    id: flow.id,
    name: flow.name,
    owner: flow.owner,
    vizportalUrlId: flow.vizportalUrlId,
    projectVizportalUrlId: flow.projectVizportalUrlId,
    containerName: flow.projectName,
    containerType: projectContainerType,
  };
}

export function getRowDataForFlow(flow: FlowsDisplayGridRowDataFragment): FlowsDisplayGridRowData {
  return {
    id: flow.id,
    name: flow.name,
    owner: flow.owner,
    vizportalUrlId: flow.vizportalUrlId,
    projectVizportalUrlId: flow.projectVizportalUrlId,
    containerName: flow.containerName,
    containerType: flow.containerType,
  };
}

type FlowsColumnFactory = ColumnFactory<GridSortProps<FlowsValidSortFields>, FlowsDisplayGridRowData, FlowsValidSortFields>;

function columns(featureFlags: FeatureFlags): ReadonlyArray<FlowsColumnFactory> {
  const cols: ReadonlyArray<FlowsColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: FlowOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.flowLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: featureFlags.personalSpaceEnabled ? 'location' : 'project',
      dataKey: featureFlags.personalSpaceEnabled ? FlowOrderField.CONTAINER_NAME : FlowOrderField.PROJECT_NAME,
      headerText: featureFlags.personalSpaceEnabled ? Messages.GridHeaderLocation() : Messages.GridHeaderProject(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createLinkOrTextCellFormatter(
        ({ containerType, containerName }) => {
          return containerType === projectContainerType ? containerName : Messages.PersonalSpaceName();
        },
        (urls, { projectVizportalUrlId, containerType }) => (projectVizportalUrlId && containerType === projectContainerType ? urls.projectPath(projectVizportalUrlId) : null)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<FlowsColumnFactory>;
}

export type FlowsDisplayGridProps = UsualDisplayGridProps<FlowsDisplayGridRowData, GraphQLError> & GridSortProps<FlowsValidSortFields>;

export function FlowsDisplayGrid(props: FlowsDisplayGridProps): JSX.Element {
  const featureFlags = React.useContext(FeatureFlagsContext);

  const cols = columns(featureFlags).map((columnFactory) => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
